import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cur } from '@/lib/currency';
import { VerifiedBalances } from '@/components/accounting/PnlBridgeSections';
import { SectionBar, pageLink } from '@/components/accounting/SectionBar';

const fmt = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Tile = ({ label, value, sub, testid, tone = '' }) => (
  <div className="rounded-lg border border-border bg-card p-3 min-w-0">
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">{label}</div>
    <div className={`text-base font-bold tabular-nums ${tone}`} data-testid={testid}>{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground truncate">{sub}</div>}
  </div>
);

const Group = ({ title, children, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <div className="text-xs font-bold text-muted-foreground">{title}</div>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">{children}</div>
  </div>
);

const Fold = ({ title, children, testid, actions }) => (
  <details className="rounded-lg border border-border bg-card px-3 py-2 group" data-testid={testid}>
    <summary className="cursor-pointer text-xs font-bold text-muted-foreground select-none flex items-center justify-between gap-2 flex-wrap"><span>{title}</span>{actions}</summary>
    <div className="pt-3 space-y-3">{children}</div>
  </details>
);

const pf = (b) => b?.profit || { net_sales: b?.sales?.net, cogs: b?.cogs, gross_profit: (b?.sales?.net || 0) - (b?.cogs || 0), operating_expenses: b?.expenses_total, net_profit: b?.net, margin: b?.net_margin };
const n2 = (v) => Number(v || 0).toFixed(2);
// Section export specs — read the same response fields the screen shows.
const tableSpec = (title, per, names, cols, data) => () => ({ title: `Branch Summary · ${title}`, subtitle: `${per?.start} to ${per?.end} · ${names || 'All branches'}`,
  filename: `branch_summary_${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 24)}_${(names || 'all-branches').replace(/[^A-Za-z0-9]+/g, '-').slice(0, 30)}_${per?.start}_${per?.end}`, cols, rows: data });
const numCol = (key, label) => ({ key, label, numeric: true, format: n2 });
const PROFIT_COLS = [{ key: 'branch', label: 'Branch' }, numCol('net_sales', 'Net sales'), numCol('cogs', 'COGS'), numCol('gross_profit', 'Gross profit'), numCol('operating_expenses', 'Operating exp.'), numCol('net_profit', 'Net profit'), numCol('margin', 'Margin %')];
const profitRows = (rows, grand) => [...(rows || []).map((b) => ({ branch: b.branch_name, ...pf(b) })), ...((rows || []).length > 1 ? [{ branch: 'TOTAL', ...pf(grand) }] : [])];

// Compact Branch Summary: Net Sales − COGS = Gross Profit − Operating Expenses = Net Profit (owner branch, excl. VAT). Same definitions as P&L.
export function BranchOverview({ grand, rows, overview, names, per }) {
  if (!grand) return null;
  const p = pf(grand);
  const s = grand.sales || {};
  const sc = overview?.supplier_credit || {};
  const v = overview?.cash_bank?.verified || {};
  const gl = overview?.gl;
  const neg = (n) => (n < 0 ? 'text-rose-600' : '');
  return (
    <div className="space-y-4" data-testid="branch-overview">
      <Group title={`Business performance${names ? ` · ${names}` : ''} · excl. VAT · costs shown in the branch that benefits`} testid="bo-profit">
        <Tile label="Net sales" value={fmt(p.net_sales)} sub={`Gross ${fmt(s.gross)} · VAT ${fmt(s.vat)}`} testid="bo-sales" />
        <Tile label="− Cost of goods" value={fmt(p.cogs)} sub="purchase-based (bought this period)" testid="bo-cogs" />
        <Tile label="= Gross profit" value={fmt(p.gross_profit)} tone={neg(p.gross_profit)} testid="bo-gross" />
        <Tile label="− Operating expenses" value={fmt(p.operating_expenses)} sub={p.paid_by_other_branches ? `incl. ${fmt(p.paid_by_other_branches)} paid by other branches` : `input VAT ${fmt(grand.expenses_vat)} excluded`} testid="bo-expenses" />
        <Tile label="= Net profit" value={fmt(p.net_profit)} tone={neg(p.net_profit)} sub={`${p.margin ?? grand.net_margin ?? 0}% margin${gl ? ` · GL ${fmt(gl.net_profit)}` : ''}`} testid="bo-net" />
      </Group>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-bold text-muted-foreground">Profit by branch</span>
        <SectionBar testId="bo-sec-profit" detailsTo={pageLink('/sales', per)} spec={tableSpec('Profit by branch', per, names, PROFIT_COLS, profitRows(rows, grand))} />
      </div>
      <BranchCompactTable rows={rows} grand={grand} />
      <Fold title="Sales — money received, platform and customer receivables (incl. VAT)" testid="bo-fold-collections"
        actions={<SectionBar testId="bo-sec-collections" detailsTo={pageLink('/sales', per)} spec={tableSpec('Sales collections', per, names,
          [{ key: 'branch', label: 'Branch' }, numCol('gross', 'Gross sales'), numCol('cash', 'Cash'), numCol('bank', 'Bank'), numCol('card_awaiting_settlement', 'Card awaiting'), numCol('platform_sales', 'Platforms'), numCol('customer_credit', 'Customer credit'), numCol('not_matched_to_payment', 'Unresolved')],
          [...(rows || []).map((b) => ({ branch: b.branch_name, gross: b.sales?.gross, ...(b.collections || {}) })), ...((rows || []).length > 1 ? [{ branch: 'TOTAL', gross: s.gross, ...(grand.collections || {}) }] : [])])} />}>
        <Collections c={grand.collections} /></Fold>
      <Fold title="Supplier purchases, credit and repayments (repaying old credit is not an expense)" testid="bo-fold-suppliers"
        actions={<SectionBar testId="bo-sec-suppliers" detailsTo="/suppliers" spec={tableSpec('Supplier credit', per, names, [{ key: 'branch', label: 'Branch' }, ...SC_COLS.map(([k, l]) => numCol(k, l))],
          [...(rows || []).filter((b) => (sc.by_branch || {})[b.branch_id]).map((b) => ({ branch: b.branch_name, ...sc.by_branch[b.branch_id] })), { branch: 'TOTAL (supplier-matched)', ...sc }])} />}>
        <SupplierCredit sc={sc} rows={rows} /></Fold>
      <Fold title="Inter-branch (costs paid by one branch for another)" testid="bo-fold-interbranch"
        actions={<SectionBar testId="bo-sec-interbranch" detailsTo={pageLink('/inter-branch-ledger', per)} spec={tableSpec('Inter-branch', per, names,
          [{ key: 'branch', label: 'Branch' }, numCol('paid_by_other_branches', 'Costs paid by other branches'), numCol('paid_for_other_branches', 'Paid for other branches')],
          (rows || []).map((b) => ({ branch: b.branch_name, ...pf(b) })))} />}>
        <table className="w-full text-[11px]" data-testid="bo-interbranch-table"><thead><tr className="text-muted-foreground"><th className="text-left">Branch</th><th className="text-right">Costs paid by other branches (counted here)</th><th className="text-right">Paid for other branches (not counted here)</th></tr></thead>
          <tbody>{(rows || []).map((b) => (<tr key={b.branch_id} className="border-t border-border"><td>{b.branch_name}</td><td className="text-right tabular-nums">{fmt(pf(b).paid_by_other_branches)}</td><td className="text-right tabular-nums">{fmt(pf(b).paid_for_other_branches)}</td></tr>))}</tbody></table>
      </Fold>
      <Fold title="Cash and bank (does not change profit)" testid="bo-fold-cash"
        actions={<SectionBar testId="bo-sec-cash" detailsTo="/bank-statements" spec={tableSpec('Cash and bank', per, names, [{ key: 'item', label: 'Item' }, numCol('value', 'Amount (SR)')],
          ['cash', 'bank'].flatMap((k) => ['opening', 'received', 'paid', 'closing'].map((f) => ({ item: `${k} (posted GL, unverified) · ${f}`, value: overview?.cash_bank?.[k]?.[f] }))))} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="bo-verified">
          <div className="rounded-lg border border-border bg-card p-3">
            <div className="text-xs font-bold text-muted-foreground mb-1">Verified balances (reconciled statement / cashier count)</div>
            <VerifiedBalances v={v} />
          </div>
          <div className="grid grid-cols-2 gap-2 content-start">
            <Tile label="Cash — calculated (GL)" value={fmt(overview?.cash_bank?.cash?.closing)} tone="text-amber-700" sub="UNVERIFIED · not available funds" testid="bo-gl-cash" />
            <Tile label="Bank — calculated (GL)" value={fmt(overview?.cash_bank?.bank?.closing)} tone="text-amber-700" sub="UNVERIFIED · not available funds" testid="bo-gl-bank" />
          </div>
        </div>
      </Fold>
    </div>
  );
}

const SC_COLS = [['opening_outstanding', 'Opening outstanding'], ['paid_immediately', 'Purchases paid immediately'], ['new_credit', 'New credit purchases'],
  ['paid_current_credit', "Paid against this period's credit"], ['paid_previous_credit', 'Paid against older credit'], ['closing_outstanding', 'Closing outstanding']];

function SupplierCredit({ sc, rows }) {
  const by = sc.by_branch || {};
  return (
    <div className="space-y-2" data-testid="bo-suppliers">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {SC_COLS.map(([k, l]) => <Tile key={k} label={l} value={fmt(sc[k])} tone={k === 'closing_outstanding' ? 'text-rose-700' : ''} testid={`bo-sup-${k}`} />)}
      </div>
      {(sc.advance_or_unmatched || 0) >= 0.01 && <div className="text-[11px] text-amber-700" data-testid="bo-sup-advance">Advances / payments without a bill: {fmt(sc.advance_or_unmatched)}</div>}
      {rows?.length > 1 && (
        <div className="overflow-x-auto"><table className="w-full text-[11px]" data-testid="bo-sup-by-branch">
          <thead><tr className="text-muted-foreground"><th className="text-left py-1">Branch</th>{SC_COLS.map(([k, l]) => <th key={k} className="text-right py-1 px-2">{l}</th>)}</tr></thead>
          <tbody>{rows.filter((b) => by[b.branch_id]).map((b) => (
            <tr key={b.branch_id} className="border-t border-border"><td className="py-1">{b.branch_name}</td>
              {SC_COLS.map(([k]) => <td key={k} className="text-right tabular-nums px-2">{fmt(by[b.branch_id][k])}</td>)}</tr>))}</tbody>
        </table></div>
      )}
      <div className="text-[10px] text-muted-foreground">{sc.basis}</div>
    </div>
  );
}

function BranchCompactTable({ rows, grand }) {
  const line = (key, name, b, bold) => {
    const p = pf(b);
    return (
      <tr key={key} className={`border-t border-border ${bold ? 'font-bold bg-muted/40' : ''}`} data-testid={`bc-row-${key}`}>
        <td className="px-3 py-1.5">{name}</td>
        {[p.net_sales, p.cogs, p.gross_profit, p.operating_expenses].map((x, i) => <td key={i} className="px-3 py-1.5 text-right tabular-nums">{fmt(x)}</td>)}
        <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${p.net_profit < 0 ? 'text-rose-600' : ''}`}>{fmt(p.net_profit)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{p.margin ?? b.net_margin ?? 0}%</td>
      </tr>
    );
  };
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-xs" data-testid="branch-compact-table">
        <thead className="bg-muted">
          <tr>{['Branch', 'Net sales', 'COGS', 'Gross profit', 'Operating exp.', 'Net profit', 'Margin'].map((h, i) => (
            <th key={h} className={`px-3 py-2 font-semibold ${i ? 'text-right' : 'text-left'}`}>{h}</th>))}</tr>
        </thead>
        <tbody>
          {(rows || []).map((b) => line(b.branch_id, b.branch_name, b))}
          {(rows || []).length > 1 && line('total', 'TOTAL', grand, true)}
        </tbody>
      </table>
    </div>
  );
}

// Sales by how they were paid — always adds up to total sales (incl. VAT).
function Collections({ c }) {
  if (!c) return null;
  const items = [['Cash received', c.cash, 'bo-cash'], ['Bank received', c.bank, 'bo-bank'], ['Card (awaiting settlement)', c.card_awaiting_settlement, 'bo-card'],
    ['Delivery platforms (receivable)', c.platform_sales, 'bo-platform-sales'], ['Customer credit (unpaid balance)', c.customer_credit, 'bo-customer']];
  return (
    <div className="space-y-1.5" data-testid="bo-collections">
      <div className="text-xs font-bold text-muted-foreground">Sales value {fmt(c.total_sales_incl_vat)} (incl. VAT) = money received + receivables + customer credit + unresolved difference</div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {items.map(([l, v, id]) => <Tile key={id} label={l} value={fmt(v)} testid={id} />)}
        <Tile label="Unresolved difference" value={fmt(c.not_matched_to_payment)} tone={Math.abs(c.not_matched_to_payment || 0) >= 0.01 ? 'text-amber-700' : ''}
          sub={c.unmatched_count ? `short-paid ${fmt(c.short_paid)} (${c.short_paid_count}) · paid above total ${fmt(c.paid_above_total)} (${c.paid_above_total_count})` : 'every sale fully explained'} testid="bo-unmatched" />
      </div>
      {c.unmatched_count > 0 && (
        <details className="text-[11px] rounded border border-amber-200 bg-amber-50/50 px-2 py-1" data-testid="bo-unmatched-details">
          <summary className="cursor-pointer font-medium text-amber-800">{c.unmatched_count} sale(s) where money received + customer credit ≠ sale total</summary>
          <table className="w-full mt-1"><tbody>
            {(c.unmatched_sales || []).map((u) => (
              <tr key={u.id} className="border-t border-amber-100" data-testid={`bo-unmatched-${u.id}`}>
                <td className="py-0.5 font-mono">{u.id.slice(0, 8)}</td><td>{u.date}</td><td className="text-right">sale {fmt(u.sale_total)}</td>
                <td className="text-right">received {fmt(u.received)}</td><td className="text-right">credit {fmt(u.customer_credit)}</td>
                <td className="text-right font-semibold">{fmt(u.difference)}</td><td className="pl-2">{u.reason === 'short_paid' ? 'Short-paid, no credit recorded' : 'Paid above total (change/tip not recorded)'}</td>
              </tr>
            ))}
          </tbody></table>
        </details>
      )}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Tile label="Platform receivable pending" value={fmt(c.platform_pending)} sub="platform sales not yet settled" testid="bo-platform" />
        {(c.platform_settled_above_recorded_sales || 0) >= 0.01 && (
          <Tile label="Settled above recorded platform sales" value={fmt(c.platform_settled_above_recorded_sales)} tone="text-amber-700"
            sub="check settlement allocation / missing platform sales" testid="bo-platform-excess" />
        )}
      </div>
    </div>
  );
}

export function AdvancedDetails({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-4">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-sm font-semibold text-primary" data-testid="bs-advanced-toggle">
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />} {open ? 'Hide' : 'Show'} advanced details (inter-branch, cash/bank calculation, drill-downs)
      </button>
      {open && <div className="space-y-5" data-testid="bs-advanced">{children}</div>}
    </div>
  );
}
